import { GripVertical, PanelBottom, PanelLeft, PanelRight } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { strings } from '../strings';
import { ATTR, useDock, type DockApi } from './context';
import {
  clampRect,
  defaultRect,
  GEOMETRY,
  isSide,
  storedTabIndex,
  zoneSizes,
  type DockZone,
  type Rect,
  type Size,
} from './layout';
import type { ToolWindowDefinition } from './registry';
import { Splitter } from './Splitter';
import { panelId, tabId, titleId, zoneId, type Placement } from './ids';
import { BottomSheet, SheetLaunchers } from './BottomSheet';
import { TabCount, WindowBody, WindowControls } from './WindowParts';

const s = strings.dock;

type DragSource = 'tab' | 'float';
type DropTarget = { kind: 'zone'; zone: DockZone } | { kind: 'tabs'; zone: DockZone; index: number };

interface DragState {
  id: string;
  source: DragSource;
  /** Pointer in workspace coordinates. */
  x: number;
  y: number;
  target: DropTarget | null;
  /** The floating rectangle at the pointer (a floating window moves with it). */
  rect: Rect;
}

const GUIDE = 44;
const GUIDE_INSET = 18;

/** Centres of the dock targets in workspace coordinates (spec 6.8.4). */
function guideCentres(workspace: Size): Record<DockZone, { x: number; y: number }> {
  return {
    left: { x: GUIDE_INSET + GUIDE / 2, y: workspace.height / 2 },
    right: { x: workspace.width - GUIDE_INSET - GUIDE / 2, y: workspace.height / 2 },
    bottom: { x: workspace.width / 2, y: workspace.height - GUIDE_INSET - GUIDE / 2 },
  };
}

function definitionOf(dock: DockApi, id: string): ToolWindowDefinition {
  return (
    dock.definitions.find((d) => d.id === id) ?? {
      id,
      title: id,
      icon: PanelBottom,
      shortcut: 0,
      defaultZone: 'bottom',
      defaultVisible: false,
      unavailable: '',
    }
  );
}

/** The window's own placement (not where a narrow screen shows it), for its controls. */
function placementOf(dock: DockApi, id: string): Placement {
  if (dock.state.maximized === id) return 'maximized';
  const window = dock.state.layout.windows[id];
  if (!window) return 'bottom';
  return window.mode === 'floating' ? 'floating' : window.zone;
}

function useWindowDrag(workspaceRef: RefObject<HTMLDivElement | null>) {
  const dock = useDock();
  const latest = useRef(dock);
  useEffect(() => {
    latest.current = dock;
  });
  const [drag, setDrag] = useState<DragState | null>(null);

  const startDrag = useCallback(
    (event: ReactPointerEvent, id: string, source: DragSource, rect?: Rect) => {
      if (event.button !== 0) return;
      if (source === 'float' && (event.target as Element).closest('button, [role="menu"]')) return;
      const element = workspaceRef.current;
      if (!element) return;
      const origin = element.getBoundingClientRect();
      const startX = event.clientX;
      const startY = event.clientY;
      const workspace = latest.current.workspace;
      const stored = latest.current.state.layout.windows[id]?.rect;
      const size = rect ?? clampRect(stored ?? defaultRect(workspace), workspace);
      const offset = rect
        ? { x: startX - origin.left - rect.x, y: startY - origin.top - rect.y }
        : { x: Math.min(80, size.width / 2), y: GEOMETRY.header / 2 };
      if (source === 'float') event.preventDefault();
      let started = false;
      let current: DragState | null = null;

      const compute = (clientX: number, clientY: number): DragState => {
        const x = clientX - origin.left;
        const y = clientY - origin.top;
        const narrow = latest.current.narrow;
        let target: DropTarget | null = null;
        const centres = guideCentres(workspace);
        for (const zone of ['left', 'right', 'bottom'] as const) {
          if (narrow && isSide(zone)) continue;
          const c = centres[zone];
          if (Math.abs(x - c.x) <= GUIDE / 2 + 6 && Math.abs(y - c.y) <= GUIDE / 2 + 6)
            target = { kind: 'zone', zone };
        }
        if (!target) {
          for (const strip of document.querySelectorAll<HTMLElement>('[data-tabstrip]')) {
            const box = strip.getBoundingClientRect();
            if (clientX < box.left || clientX > box.right || clientY < box.top || clientY > box.bottom)
              continue;
            const zone = strip.dataset.tabstrip as DockZone;
            const vertical = strip.dataset.vertical === 'true';
            const tabs = [...strip.querySelectorAll<HTMLElement>('[role="tab"]')].filter(
              (tab) => tab.getAttribute(ATTR.tab) !== id,
            );
            const index = tabs.filter((tab) => {
              const r = tab.getBoundingClientRect();
              return vertical ? r.top + r.height / 2 < clientY : r.left + r.width / 2 < clientX;
            }).length;
            target = { kind: 'tabs', zone, index };
          }
        }
        let moved = clampRect(
          { x: x - offset.x, y: y - offset.y, width: size.width, height: size.height },
          workspace,
        );
        if (source === 'tab') {
          // A window torn off a dock floats wholly inside the workspace when it fits.
          moved = {
            ...moved,
            x: Math.max(0, Math.min(moved.x, workspace.width - moved.width)),
            y: Math.max(0, Math.min(moved.y, workspace.height - moved.height)),
          };
        }
        return { id, source, x, y, target, rect: moved };
      };

      const onMove = (move: PointerEvent) => {
        if (!started) {
          if (Math.hypot(move.clientX - startX, move.clientY - startY) < 4) return;
          started = true;
          document.body.classList.add('dock-dragging');
        }
        current = compute(move.clientX, move.clientY);
        setDrag(current);
      };
      const cleanup = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        window.removeEventListener('keydown', onKey, true);
        document.body.classList.remove('dock-dragging');
        setDrag(null);
      };
      const onUp = () => {
        cleanup();
        if (!started || !current) return;
        const d = latest.current;
        const { target } = current;
        if (target?.kind === 'zone') d.dock(id, target.zone);
        else if (target?.kind === 'tabs')
          d.dock(
            id,
            target.zone,
            storedTabIndex(
              d.state.layout.zones[target.zone].tabs,
              d.effective.zones[target.zone].tabs,
              target.index,
              id,
            ),
          );
        else if (source === 'float') d.move(id, current.rect);
        else if (!d.narrow) d.float(id, current.rect);
      };
      const onCancel = () => cleanup();
      const onKey = (key: globalThis.KeyboardEvent) => {
        if (key.key !== 'Escape') return;
        key.preventDefault();
        key.stopPropagation();
        started = false;
        cleanup();
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
      window.addEventListener('keydown', onKey, true);
    },
    [workspaceRef],
  );
  return { drag, startDrag };
}

type StartDrag = ReturnType<typeof useWindowDrag>['startDrag'];

/** A dock zone: its tab group and the active window (spec 6.8.1, 6.8.7). */
function DockZoneView({ zone, size, startDrag }: { zone: DockZone; size: number; startDrag: StartDrag }) {
  const dock = useDock();
  const { tabs, active, collapsed } = dock.effective.zones[zone];
  const verticalTabs = collapsed && isSide(zone);
  const style = zone === 'bottom' ? { height: size } : { width: size };
  const activeDefinition = active ? definitionOf(dock, active) : null;

  const onTabKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = active ? tabs.indexOf(active) : -1;
    const keys = verticalTabs
      ? { prev: 'ArrowUp', next: 'ArrowDown' }
      : { prev: 'ArrowLeft', next: 'ArrowRight' };
    let next = -1;
    if (event.key === keys.prev) next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === keys.next) next = (index + 1) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0 || event.ctrlKey || event.metaKey || event.altKey) return;
    event.preventDefault();
    const id = tabs[next];
    dock.activate(id);
    requestAnimationFrame(() => document.getElementById(tabId(id))?.focus());
  };

  return (
    <div
      id={zoneId(zone)}
      role="group"
      aria-label={s.zone[zone]}
      data-region={`dock-${zone}`}
      data-zone={zone}
      tabIndex={-1}
      className={`dock-zone dock-zone-${zone}${collapsed ? ' is-collapsed' : ''}`}
      style={style}
    >
      <div className="tw-head" data-tabstrip={zone} data-vertical={verticalTabs ? 'true' : 'false'}>
        <div
          role="tablist"
          aria-label={s.zoneTabs(s.zone[zone])}
          aria-orientation={verticalTabs ? 'vertical' : 'horizontal'}
          className="tw-tabs"
          onKeyDown={onTabKeyDown}
        >
          {tabs.map((id) => {
            const definition = definitionOf(dock, id);
            const Icon = definition.icon;
            const selected = id === active;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                id={tabId(id)}
                aria-selected={selected}
                aria-controls={selected && !collapsed ? panelId(id) : undefined}
                aria-label={verticalTabs ? definition.title : undefined}
                title={verticalTabs ? definition.title : undefined}
                tabIndex={selected ? 0 : -1}
                className="tw-tab"
                {...{ [ATTR.tab]: id }}
                onClick={() => {
                  if (collapsed) dock.collapse(zone, false);
                  dock.activate(id);
                }}
                onDoubleClick={() => dock.toggleMaximize(id)}
                onPointerDown={(event) => startDrag(event, id, 'tab')}
              >
                <Icon aria-hidden className="size-3.5 shrink-0" />
                {!verticalTabs && <span className="truncate">{definition.title}</span>}
                {!verticalTabs && <TabCount badge={dock.badges.get(id)} />}
              </button>
            );
          })}
        </div>
        {activeDefinition && !verticalTabs && (
          <WindowControls
            definition={activeDefinition}
            placement={placementOf(dock, activeDefinition.id)}
            menuPlacement={zone === 'bottom' ? 'above-end' : 'below-end'}
          />
        )}
      </div>
      {active && !collapsed && (
        <div role="tabpanel" id={panelId(active)} aria-labelledby={tabId(active)} className="tw-panel">
          <section
            role="region"
            aria-labelledby={titleId(active)}
            tabIndex={-1}
            className="tw-window"
            {...{ [ATTR.frame]: active }}
            data-placement="docked"
            data-zone={zone}
          >
            <h2 id={titleId(active)} className="sr-only">
              {activeDefinition?.title}
            </h2>
            <WindowBody id={active} />
          </section>
        </div>
      )}
    </div>
  );
}

const HANDLES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'] as const;
type Handle = (typeof HANDLES)[number];

/** Raises a floating window when it gets a pointer or focus (native events: the content is a portal). */
function useBringToFront(ref: RefObject<HTMLDivElement | null>, id: string, front: boolean) {
  const dock = useDock();
  const latest = useRef({ dock, front });
  useEffect(() => {
    latest.current = { dock, front };
  });
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const raise = () => {
      if (!latest.current.front) latest.current.dock.activate(id);
    };
    element.addEventListener('pointerdown', raise, true);
    element.addEventListener('focusin', raise);
    return () => {
      element.removeEventListener('pointerdown', raise, true);
      element.removeEventListener('focusin', raise);
    };
  }, [ref, id]);
}

function TitleBar({
  definition,
  placement,
  onPointerDown,
}: {
  definition: ToolWindowDefinition;
  placement: Placement;
  onPointerDown?: (event: ReactPointerEvent<HTMLDivElement>) => void;
}) {
  const dock = useDock();
  const Icon = definition.icon;
  return (
    <div
      className={`tw-head tw-titlebar${onPointerDown ? ' is-draggable' : ''}`}
      onPointerDown={onPointerDown}
      onDoubleClick={(event) => {
        if (!(event.target as Element).closest('button')) dock.toggleMaximize(definition.id);
      }}
    >
      {onPointerDown && <GripVertical aria-hidden className="ml-1 size-3.5 shrink-0 text-fg-faint" />}
      <h2 id={titleId(definition.id)} className="tw-title">
        <Icon aria-hidden className="size-3.5 shrink-0" />
        <span className="truncate">{definition.title}</span>
        <TabCount badge={dock.badges.get(definition.id)} />
      </h2>
      <WindowControls definition={definition} placement={placement} />
    </div>
  );
}

/** A floating window (spec 6.8.3, 6.8.4): moves with its title bar, resizes from every edge and corner. */
function FloatingWindow({
  id,
  index,
  front,
  drag,
  startDrag,
}: {
  id: string;
  index: number;
  front: boolean;
  drag: DragState | null;
  startDrag: StartDrag;
}) {
  const dock = useDock();
  const ref = useRef<HTMLDivElement>(null);
  const [resizing, setResizing] = useState<Rect | null>(null);
  const definition = definitionOf(dock, id);
  const stored = dock.state.layout.windows[id]?.rect ?? defaultRect(dock.workspace);
  const placed = clampRect(stored, dock.workspace);
  const rect = drag?.id === id && drag.source === 'float' ? drag.rect : (resizing ?? placed);
  useBringToFront(ref, id, front);

  const startResize = (event: ReactPointerEvent<HTMLDivElement>, handle: Handle) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const start = { x: event.clientX, y: event.clientY, rect: placed };
    const min = GEOMETRY.floating;
    let current = placed;
    const onMove = (move: PointerEvent) => {
      const dx = move.clientX - start.x;
      const dy = move.clientY - start.y;
      let { x, y, width, height } = start.rect;
      if (handle.includes('e')) width = Math.max(min.minWidth, width + dx);
      if (handle.includes('s')) height = Math.max(min.minHeight, height + dy);
      if (handle.includes('w')) {
        const w = Math.max(min.minWidth, width - dx);
        x += width - w;
        width = w;
      }
      if (handle.includes('n')) {
        const h = Math.max(min.minHeight, height - dy);
        y += height - h;
        height = h;
      }
      current = clampRect({ x, y, width, height }, dock.workspace);
      setResizing(current);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.classList.remove('dock-resizing');
      setResizing(null);
      dock.move(id, current);
    };
    document.body.classList.add('dock-resizing');
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId(id)}
      tabIndex={-1}
      data-region="floating"
      data-stack={index}
      data-placement="floating"
      {...{ [ATTR.frame]: id }}
      className="tw-float"
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, zIndex: 30 + index }}
    >
      <TitleBar
        definition={definition}
        placement="floating"
        onPointerDown={(event) => startDrag(event, id, 'float', rect)}
      />
      <WindowBody id={id} />
      {HANDLES.map((handle) => (
        <div
          key={handle}
          aria-hidden
          className={`tw-resize tw-resize-${handle}`}
          onPointerDown={(event) => startResize(event, handle)}
        />
      ))}
    </div>
  );
}

/** The maximized window: fills the workspace above the page and docks (spec 6.8.3). */
function MaximizedWindow({ id }: { id: string }) {
  const dock = useDock();
  const definition = definitionOf(dock, id);
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId(id)}
      tabIndex={-1}
      data-region="maximized"
      data-placement="maximized"
      {...{ [ATTR.frame]: id }}
      className="tw-max"
    >
      <TitleBar definition={definition} placement="maximized" />
      <WindowBody id={id} />
    </div>
  );
}

/** Dock targets, the preview of the hovered one, and the dragged tab (spec 6.8.4). Mouse only. */
function DragOverlay({ drag, sizes }: { drag: DragState; sizes: Record<DockZone, number> }) {
  const dock = useDock();
  const workspace = dock.workspace;
  const centres = guideCentres(workspace);
  const zones = (['left', 'right', 'bottom'] as const).filter((zone) => !(dock.narrow && isSide(zone)));
  const sideSize = (zone: DockZone) =>
    sizes[zone] > GEOMETRY.collapsedSide
      ? sizes[zone]
      : Math.min(GEOMETRY.side.size, Math.floor(workspace.width * GEOMETRY.side.maxFraction));
  let preview: Rect | null = null;
  const target = drag.target;
  if (target?.kind === 'zone') {
    if (target.zone === 'left') preview = { x: 0, y: 0, width: sideSize('left'), height: workspace.height };
    else if (target.zone === 'right') {
      const width = sideSize('right');
      preview = { x: workspace.width - width, y: 0, width, height: workspace.height };
    } else {
      const height =
        sizes.bottom > GEOMETRY.header
          ? sizes.bottom
          : Math.min(GEOMETRY.bottom.size, Math.floor(workspace.height * GEOMETRY.bottom.maxFraction));
      preview = {
        x: sizes.left,
        y: workspace.height - height,
        width: workspace.width - sizes.left - sizes.right,
        height,
      };
    }
  }
  const Icons = { left: PanelLeft, right: PanelRight, bottom: PanelBottom };
  return (
    <div aria-hidden className="dock-overlay">
      {preview && (
        <div
          className="dock-preview"
          style={{ left: preview.x, top: preview.y, width: preview.width, height: preview.height }}
        />
      )}
      {zones.map((zone) => {
        const Icon = Icons[zone];
        const c = centres[zone];
        const on = target?.kind === 'zone' && target.zone === zone;
        return (
          <span
            key={zone}
            data-dock-target={zone}
            className={`dock-guide${on ? ' is-on' : ''}`}
            style={{ left: c.x - GUIDE / 2, top: c.y - GUIDE / 2, width: GUIDE, height: GUIDE }}
          >
            <Icon className="size-5" />
          </span>
        );
      })}
      {drag.source === 'tab' && (
        <div
          className="dock-ghost"
          style={{ left: Math.min(drag.x + 12, workspace.width - 140), top: drag.y + 10 }}
        >
          {definitionOf(dock, drag.id).title}
        </div>
      )}
    </div>
  );
}

/**
 * The workspace (spec 6.1.3, 6.8): the left dock, the page above the bottom dock, the right dock, floating
 * windows, the maximized window and the drag overlay. `children` is the page.
 */
export function Workspace({ children }: { children: ReactNode }) {
  const dock = useDock();
  const ref = useRef<HTMLDivElement>(null);
  const { setWorkspace } = dock;

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const measure = (width: number, height: number) => {
      // jsdom reports no layout: fall back to the viewport minus the shell's bars.
      setWorkspace({
        width: Math.round(width || Math.max(0, window.innerWidth - 72)),
        height: Math.round(height || Math.max(0, window.innerHeight - 74)),
      });
    };
    const box = element.getBoundingClientRect();
    measure(box.width, box.height);
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) measure(r.width, r.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [setWorkspace]);

  const { drag, startDrag } = useWindowDrag(ref);
  const { effective } = dock;
  const sizes = zoneSizes(dock.state.layout, effective, dock.workspace);
  const maximized = effective.maximized;
  const floating = effective.floating;
  // Floating windows keep a stable DOM order (moving a focused node would blur it); z-index stacks them.
  const floatingByDom = [...floating].sort();

  return (
    <div ref={ref} className="workspace">
      <div className="workspace-row" inert={maximized ? true : undefined}>
        {sizes.left > 0 && (
          <>
            <DockZoneView zone="left" size={sizes.left} startDrag={startDrag} />
            <Splitter zone="left" size={sizes.left} otherSide={sizes.right} />
          </>
        )}
        <div className="workspace-center">
          {dock.phone && <SheetLaunchers />}
          <main id="main" data-region="main" tabIndex={-1} className="workspace-page">
            {children}
          </main>
          {sizes.bottom > 0 && (
            <>
              <Splitter zone="bottom" size={sizes.bottom} otherSide={0} />
              <DockZoneView zone="bottom" size={sizes.bottom} startDrag={startDrag} />
            </>
          )}
        </div>
        {sizes.right > 0 && (
          <>
            <Splitter zone="right" size={sizes.right} otherSide={sizes.left} />
            <DockZoneView zone="right" size={sizes.right} startDrag={startDrag} />
          </>
        )}
      </div>
      {floatingByDom.map((id) => (
        <FloatingWindow
          key={id}
          id={id}
          index={floating.indexOf(id)}
          front={floating[floating.length - 1] === id}
          drag={drag}
          startDrag={startDrag}
        />
      ))}
      {maximized && <MaximizedWindow id={maximized} />}
      {drag && <DragOverlay drag={drag} sizes={sizes} />}
      {dock.phone && dock.sheet && effective.displayed.has(dock.sheet) && <BottomSheet id={dock.sheet} />}
    </div>
  );
}
